<template>
  <li class="tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-gray-100 tw-px-6 tw-py-4">
    <div class="tw-flex tw-items-start tw-justify-between tw-gap-4">
      <WdsCheckbox
        data-test="checklist-check-checkbox"
        :checked="loading"
        :disabled="!canDecide || loading || notApplicableFormOpen"
        :sub-label="check.helpText ?? undefined"
        @update:checked="setChecked"
      >
        <span class="tw-flex tw-items-center tw-gap-2">
          {{ check.label }}
          <WdsTag v-if="!check.blocking" sm gray :label="$t('Optional')" />
        </span>
      </WdsCheckbox>

      <div class="tw-flex tw-flex-none tw-items-center">
        <WdsSpinner v-if="loading" data-test="checklist-check-saving" indeterminate />
        <WdsButtonText
          v-else-if="canMarkNotApplicable"
          data-test="checklist-not-applicable-open"
          @click="notApplicableFormOpen = true"
        >
          {{ $t('Not applicable') }}
        </WdsButtonText>
      </div>
    </div>

    <div v-if="notApplicableFormOpen" class="tw-flex tw-items-center tw-gap-2 tw-pl-7">
      <div class="tw-min-w-0 tw-flex-1">
        <WdsInput
          v-model="notApplicableReason"
          data-test="checklist-not-applicable-reason"
          :placeholder="$t('Reason it doesn\'t apply')"
          :aria-label="$t('Reason it doesn\'t apply')"
          :disabled="loading"
          @keydown.enter.prevent="markNotApplicable"
          @keydown.esc="closeNotApplicableForm"
        />
      </div>
      <WdsButton
        data-test="checklist-not-applicable-submit"
        primary
        type="button"
        :disabled="notApplicableReason.trim() === '' || loading"
        @click="markNotApplicable"
      >
        {{ $t('Save') }}
      </WdsButton>
      <WdsButton data-test="checklist-not-applicable-cancel" type="button" :disabled="loading" @click="closeNotApplicableForm">
        {{ $t('Cancel') }}
      </WdsButton>
    </div>
  </li>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { WdsButton, WdsButtonText, WdsCheckbox, WdsInput, WdsSpinner, WdsTag } from '@worksome/wds-components'
import { ChecklistCheckRowFragment } from './fragments'
import { useChecklistCheck } from './useChecklistCheck'
import { FragmentType } from '@/gql'
import { ChecklistCheckState } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = defineProps<{
  check: FragmentType<typeof ChecklistCheckRowFragment>
  canDecide: boolean
}>()

const notApplicableFormOpen = ref(false)
const notApplicableReason = ref('')

const { check, loading, setState } = useChecklistCheck(() => props.check)

const canMarkNotApplicable = computed(
  () => props.canDecide && check.value.allowsNotApplicable && !notApplicableFormOpen.value && !loading.value,
)

function setChecked(checked: boolean) {
  if (checked) {
    setState(ChecklistCheckState.Confirmed)
  }
}

function closeNotApplicableForm() {
  notApplicableFormOpen.value = false
  notApplicableReason.value = ''
}

async function markNotApplicable() {
  const reason = notApplicableReason.value.trim()

  if (reason === '' || loading.value) {
    return
  }

  if (await setState(ChecklistCheckState.NotApplicable, reason)) {
    closeNotApplicableForm()
  }
}
</script>
